// src/pages/SmartForecast/SmartForecastDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrainCircuit,
  AlertCircle,
  Sparkles,
  Trophy,
  Award,
  Users,
  AlertCircle as AlertCircleIcon,
} from "lucide-react";

import { merchantService } from '../../../services/apiService';
import SmartForecastHeader from './components/SmartForecastHeader';
import AIAnalysisSection from './components/AIAnalysisSection';
import DetailedAnalysisSection from './components/DetailedAnalysisSection';
import Customer360Section from './components/Customer360Section';
import PaymentAnalyticsSection from './components/PaymentAnalyticsSection';
import { setMerchantId as setGlobalMerchantId } from "../../../features/global/globalSlice";

// --- BẢN ĐỒ STYLE CHO CÁC NHÓM KHÁCH HÀNG ---
const SEGMENT_STYLES = {
  VIP: { color: "bg-yellow-500", text: "text-yellow-600", icon: Trophy },
  Loyalty: { color: "bg-green-500", text: "text-green-600", icon: Award },
  "Vãng Lai": { color: "bg-blue-500", text: "text-blue-600", icon: Users },
  Churn: { color: "bg-red-500", text: "text-red-600", icon: AlertCircleIcon },
  Default: { color: "bg-slate-500", text: "text-slate-600", icon: Users },
};

// --- DỮ LIỆU GIẢ LẬP (MOCK DATA) CHO PHẦN CHƯA CÓ API ---
const demographicData = {
  gender: [
    { name: "Nữ", percent: 65, color: "bg-pink-400" },
    { name: "Nam", percent: 35, color: "bg-blue-400" },
  ],
  age: [
    { range: "18-24", percent: 45, spend: 8500000 },
    { range: "25-34", percent: 35, spend: 12000000 },
    { range: "35-44", percent: 15, spend: 4500000 },
    { range: "45+", percent: 5, spend: 1200000 },
  ],
};

// Data thanh toán đã được tích hợp qua PaymentAnalyticsSection

// --- HÀM HELPER XỬ LÝ NGÀY THÁNG ---
const getYesterdayAndToday = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const format = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return { startDate: format(yesterday), endDate: format(today) };
};

export default function SmartForecastDashboard() {
  const dispatch = useDispatch();
  const currentMerchantId = useSelector((state) => state.global.merchant_id);
  const { startDate: initStartDate, endDate: initEndDate } = getYesterdayAndToday();
  const currentDate = new Date();
  const currentMonthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;

  // State quản lý Input Tìm kiếm
  const [merchantId, setMerchantId] = useState(currentMerchantId || "");
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);

  // State Trạng thái chung
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State Data Chính
  const [overviewData, setOverviewData] = useState(null);
  const [segmentData, setSegmentData] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [revenueData, setRevenueData] = useState(null);

  // State Data Phân tích chuyên sâu (Growth)
  const [growthData, setGrowthData] = useState(null);
  const [isGrowthLoading, setGrowthLoading] = useState(false);
  const [growthError, setGrowthError] = useState(null);

  // API 1: Fetch dữ liệu Tổng quan & Report AI
  const fetchData = useCallback(
    async (idToFetch) => {
      const targetId = idToFetch || currentMerchantId;
      if (!targetId) {
        setError("Vui lòng nhập Merchant ID");
        return;
      }

      setMerchantId(targetId);

      setLoading(true);
      setError(null);
      setOverviewData(null);
      setSegmentData([]);
      setAiAnalysis(null);
      setRevenueData(null);

      try {
        const retentionRes = await merchantService
          .getCustomerRetention(targetId)
          .catch(() => null);
        const segmentRes = await merchantService
          .getSegmentSummary(targetId)
          .catch(() => null);

        // Delay nhẹ để tạo cảm giác AI đang xử lý (tùy chọn)
        await new Promise((resolve) => setTimeout(resolve, 300));
        
        const revenueRes = await merchantService
          .getRevenueReport({ merchant_id: targetId, month: currentMonthStr })
          .catch(() => null);

        const aiRes = await merchantService
          .getAiReportLatest({ merchant_id: targetId })
          .catch(() => null);

        // Xử lý dữ liệu Retention
        if (retentionRes) setOverviewData(retentionRes?.data || retentionRes);

        // Xử lý dữ liệu Segment (Gắn thêm màu sắc & icon)
        if (segmentRes) {
          const rawSegmentsArray = Array.isArray(segmentRes.data)
            ? segmentRes.data
            : Array.isArray(segmentRes.data?.data)
              ? segmentRes.data.data
              : [];
          const formattedSegments = rawSegmentsArray.map((item) => ({
            ...item,
            ...(SEGMENT_STYLES[item.segment_name] || SEGMENT_STYLES["Default"]),
          }));
          setSegmentData(formattedSegments);
        }

        // Xử lý dữ liệu Revenue
        if (revenueRes) {
          const revData = revenueRes.data || revenueRes;
          if (revData && revData.summary) setRevenueData(revData);
        }

        // Xử lý dữ liệu AI
        if (aiRes?.data && aiRes.data.found) setAiAnalysis(aiRes.data);

        // Báo lỗi nếu tất cả API đều xịt
        if (!retentionRes && !segmentRes && !revenueRes && !aiRes) {
          setError("Không tìm thấy dữ liệu hoặc có lỗi kết nối đến máy chủ.");
        }
      } catch (err) {
        console.error(err);
        setError("Đã có lỗi không xác định xảy ra. Vui lòng thử lại!");
      } finally {
        setLoading(false);
      }
    },
    [currentMerchantId, currentMonthStr]
  );

  // API 2: Fetch dữ liệu Phân Tích Chuyên Sâu (Growth Metric) theo Date Range
  const fetchGrowthData = useCallback(
    async (idToFetch) => {
      const targetId = idToFetch || currentMerchantId;
      if (!targetId) {
        setGrowthError("Vui lòng nhập Merchant ID");
        return;
      }

      setGrowthLoading(true);
      setGrowthError(null);
      setGrowthData(null);

      try {
        const growthRes = await merchantService.getGrowthScaleMetric(
          targetId,
          startDate,
          endDate
        );

        if (growthRes && growthRes.data) {
          setGrowthData(growthRes.data);
        } else {
          setGrowthError("Không tìm thấy dữ liệu tăng trưởng trong khoảng thời gian này.");
        }
      } catch (err) {
        console.error("Lỗi khi fetch dữ liệu Growth & Scale:", err);
        setGrowthError("Không tìm thấy dữ liệu tăng trưởng trong khoảng thời gian này.");
      } finally {
        setGrowthLoading(false);
      }
    },
    [currentMerchantId, startDate, endDate]
  );

  // Hook 1: Kích hoạt khi merchant đang xem thay đổi trong Redux
  useEffect(() => {
    setMerchantId(currentMerchantId || "");

    if (currentMerchantId) {
      const loadDataSequentially = async () => {
        await fetchData(currentMerchantId);
        await fetchGrowthData(currentMerchantId);
      };
      loadDataSequentially();
      return;
    }

    setOverviewData(null);
    setSegmentData([]);
    setAiAnalysis(null);
    setRevenueData(null);
    setGrowthData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMerchantId]);

  // Hook 2: Lắng nghe khi user thay đổi bộ lọc Ngày (Từ ngày - Đến ngày)
  useEffect(() => {
    if (currentMerchantId) {
      fetchGrowthData(currentMerchantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, currentMerchantId]);

  // Handle Event khi ấn nút Tìm Kiếm
  const handleSearch = (e) => {
    e.preventDefault();
    const normalizedMerchantId = merchantId.trim();

    if (!normalizedMerchantId) {
      setError("Vui lòng nhập Merchant ID");
      return;
    }

    if (normalizedMerchantId !== currentMerchantId) {
      dispatch(setGlobalMerchantId(normalizedMerchantId));
      return;
    }

    fetchData(normalizedMerchantId);
    fetchGrowthData(normalizedMerchantId);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 pb-12">
      <SmartForecastHeader
        merchantId={merchantId}
        setMerchantId={setMerchantId}
        handleSearch={handleSearch}
        loading={loading}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* State 1: Đang Tải Data */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-4 border-slate-200 border-t-purple-600 animate-spin"></div>
              <BrainCircuit className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-purple-600 h-6 w-6" />
            </div>
            <p className="mt-4 text-slate-500 font-medium animate-pulse">
              AI đang xử lý & tải dữ liệu...
            </p>
          </div>
        )}

        {/* State 2: Có Lỗi (API Toàn trang) */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 border border-red-200 font-medium shadow-sm">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* State 3: Màn hình chờ (Chưa nhập ID) */}
        {!loading && !error && !aiAnalysis && !overviewData && (
          <div className="text-center py-20 opacity-60">
            <div className="inline-block p-6 rounded-full bg-slate-200 mb-4 shadow-inner">
              <Sparkles className="h-12 w-12 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-600">
              Sẵn sàng phân tích dữ liệu
            </h3>
            <p className="text-slate-400 mt-2 font-medium">
              Nhập Merchant ID và nhấn Phân tích để hiển thị các chỉ số.
            </p>
          </div>
        )}

        {/* State 4: Hiển thị Dashboard (Khi có dữ liệu) */}
        {!loading && (aiAnalysis || overviewData) && (
          <div className="space-y-8 animate-fade-in-up">
            
            {/* Phân hệ 1: Sức khỏe, Chỉ số dẫn dắt, Đề xuất AI */}
            <AIAnalysisSection
              aiAnalysis={aiAnalysis}
              revenueData={revenueData}
            />

            {/* Phân hệ 2: Phân Tích Chuyên Sâu & Xu hướng Growth */}
            <DetailedAnalysisSection
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              growthData={growthData}
              isGrowthLoading={isGrowthLoading}
              growthError={growthError}
            />

            {/* Phân hệ 3: Phân tích Phương thức & Ngân hàng
            <PaymentAnalyticsSection 
              merchantId={currentMerchantId} 
              startDate={startDate} 
              endDate={endDate} 
            /> */}

            {/* Phân hệ 4: Chân dung Khách hàng & Hành vi */}
            <Customer360Section
              overviewData={overviewData}
              segmentData={segmentData}
              demographicData={demographicData}
            />
            
          </div>
        )}
      </main>
    </div>
  );
}
